import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Input from '@material-ui/core/Input';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Icon from '@afosto/app-components-react/Icon';
import Popper from '@afosto/components-react/Popper';
import clsx from 'clsx';
import styled from 'styled-components';

const QuantityBoxBG = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${props => props.theme.spacing(7)}px;
  height: ${props => props.theme.spacing(7)}px;
  background-color: ${props => props.theme.palette.grey[100]};
  font-size: ${props => props.theme.typography.h6.fontSize};
  font-weight: 700;

  &.color-primary {
    background-color: ${props => props.theme.palette.primary.main};
    color: ${props => props.theme.palette.primary.contrastText};
  }
  &.color-secondary {
    background-color: ${props => props.theme.palette.secondary.main};
    color: ${props => props.theme.palette.secondary.contrastText};
  }
  &.color-error {
    background-color: ${props => props.theme.palette.error.main};
    color: ${props => props.theme.palette.error.contrastText};
  }
  &.color-success {
    background-color: ${props => props.theme.palette.success.main};
    color: ${props => props.theme.palette.success.contrastText};
  }
  &.color-warning {
    background-color: ${props => props.theme.palette.warning.main};
    color: ${props => props.theme.palette.warning.contrastText};
  }
  &.color-none {
    background: none;
  }
`;

const QuantityPopover = styled.div`
  display: grid;
  grid-template-columns: repeat(3, ${150 / 3}px);
`;

const QuantityInput = styled(Input)`
  && {
    .MuiInput-input {
      text-align: center;
    }

    &.editable {
      background-color: ${props => props.theme.palette.grey[100]};
    }
  }
`;

const QuantityButton = styled(Button)`
  && {
    border-radius: 0;
    padding: ${props => props.theme.spacing(1.5)}px;
    min-width: auto;
  }
`;

const QuantityBox = ({
  quantity,
  color,
  label,
  allowEdit,
  onChange,
  onPlus,
  onMinus,
  position,
  showLabel,
}) => {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const [popperIsOpen, setPopperIsOpen] = useState(false);
  const anchorEL = useRef(null);

  /**
   * handle closing the popper menu.
   * @param {Event} event
   */
  const handleClose = event => {
    if (anchorEL.current.contains(event.target)) {
      return;
    }

    setPopperIsOpen(false);
  };

  const handleChangeQuantity = event => {
    if (onChange) {
      onChange(event, event.target?.value);
    }
  };

  const handleClickQuantity = event => event.target?.select();

  return (
    <div>
      {showLabel && <Typography variant="overline">{label}</Typography>}

      <QuantityBoxBG
        className={clsx(color ? `color-${color}` : null)}
        onClick={() => setPopperIsOpen(!popperIsOpen)}
        ref={anchorEL}
      >
        {quantity}
      </QuantityBoxBG>
      {allowEdit && (
        <Popper
          open={popperIsOpen}
          anchorEl={anchorEL}
          handleClose={handleClose}
          position={position}
        >
          <ClickAwayListener onClickAway={handleClose}>
            <QuantityPopover>
              <QuantityButton onClick={onMinus} color="default">
                <Icon icon={['fal', 'minus']} />
              </QuantityButton>
              <QuantityInput
                className={clsx(allowEdit && onChange ? 'editable' : null)}
                value={quantity}
                type="number"
                variant="outlined"
                readOnly={!allowEdit || !onChange}
                onChange={handleChangeQuantity}
                onClick={handleClickQuantity}
                disableUnderline={!allowEdit || !onChange}
                autoFocus={allowEdit && onChange && !isMobile}
              />
              <QuantityButton onClick={onPlus} color="default">
                <Icon icon={['fal', 'plus']} />
              </QuantityButton>
            </QuantityPopover>
          </ClickAwayListener>
        </Popper>
      )}
    </div>
  );
};

QuantityBox.propTypes = {
  quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  color: PropTypes.oneOf(['none', 'primary', 'secondary', 'success', 'warning', 'default']),
  showLabel: PropTypes.bool,
  label: PropTypes.string,
  allowEdit: PropTypes.bool,
  onChange: PropTypes.func,
  onPlus: PropTypes.func,
  onMinus: PropTypes.func,
  position: PropTypes.oneOf([
    'bottom-end',
    'bottom-start',
    'bottom',
    'left-end',
    'left-start',
    'left',
    'right-end',
    'right-start',
    'right',
    'top-end',
    'top-start',
    'top',
  ]),
};

QuantityBox.defaultProps = {
  quantity: 0,
  color: undefined,
  label: undefined,
  showLabel: true,
  allowEdit: false,
  onChange: undefined,
  onPlus: undefined,
  onMinus: undefined,
  position: 'top',
};

export default QuantityBox;
